import { useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown, ChevronUp } from 'lucide-react';

interface ResizableDividerProps {
  timelineHeight: number;
  onResize: (newTimelineHeight: number) => void;
  collapsed: boolean;
  onToggleCollapse: () => void;
  containerRef: React.RefObject<HTMLDivElement | null>;
  minPreview?: number;
  minTimeline?: number;
}

export function ResizableDivider({
  timelineHeight,
  onResize,
  collapsed,
  onToggleCollapse,
  containerRef,
  minPreview = 200,
  minTimeline = 120,
}: ResizableDividerProps) {
  const { t } = useTranslation();
  const draggingRef = useRef(false);

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    draggingRef.current = true;
    const startY = e.clientY;
    const startHeight = timelineHeight;

    function onMouseMove(ev: MouseEvent) {
      if (!draggingRef.current || !containerRef.current) return;
      const containerHeight = containerRef.current.clientHeight;
      // header is 56px (h-14)
      const availableHeight = containerHeight - 56 - 6; // minus header and divider
      const delta = startY - ev.clientY;
      const newHeight = Math.max(
        minTimeline,
        Math.min(availableHeight - minPreview, startHeight + delta),
      );
      onResize(newHeight);
    }

    function onMouseUp() {
      draggingRef.current = false;
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    }

    document.body.style.cursor = 'row-resize';
    document.body.style.userSelect = 'none';
    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
  }, [timelineHeight, onResize, containerRef, minPreview, minTimeline]);

  return (
    <div
      className="relative flex items-center justify-center shrink-0 cursor-row-resize group border-y border-[#2A2A2E] bg-[#252529]"
      style={{ height: 6 }}
      onMouseDown={handleMouseDown}
    >
      {/* Grip indicator */}
      <div className="w-8 h-1 rounded-full bg-[var(--user-page-text)] opacity-20 group-hover:opacity-40 transition-opacity" />

      {/* Collapse toggle */}
      <button
        onClick={(e) => {
          e.stopPropagation();
          onToggleCollapse();
        }}
        className="absolute end-2 top-1/2 -translate-y-1/2 p-0.5 rounded hover:bg-[var(--user-nav-hover-bg)] text-[var(--user-page-text)] opacity-40 hover:opacity-80 transition-all z-10 cursor-pointer"
        title={t('editor.collapseTimeline', 'Show/hide timeline')}
        onMouseDown={(e) => e.stopPropagation()}
      >
        {collapsed ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
      </button>
    </div>
  );
}
